import { useEffect, useState, useCallback } from "react";
import {
  FiActivity,
  FiBarChart2,
  FiUsers,
  FiTrendingUp,
  FiPlus,
  FiEdit,
} from "react-icons/fi";
import PageMeta from "../../tailadmin/components/common/PageMeta";
import { useAuth } from "../../context/AuthContext";
import reports from "../../services/reports";
import { NetworkGraphData, PlatformOverviewResponse, User } from "../../types";
import Skeleton from "../../components/ui/Skeleton";
import DashboardBarChart from "../../components/charts/DashboardBarChart";
import NetworkGraph from "../../components/charts/NetworkGraph";
import NewProfessionalModal from "../../components/admin/NewProfessionalModal";
import NewStudentModal from "../../components/students/modals/NewStudentModal";

const filterOptions = [
  { label: "7 dias", value: 7 },
  { label: "15 dias", value: 15 },
  { label: "30 dias", value: 30 },
  { label: "45 dias", value: 45 },
  { label: "90 dias", value: 90 },
];

export default function AdminOverview() {
  const { getUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<number | null>(30);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [overview, setOverview] = useState<PlatformOverviewResponse | null>(
    null,
  );
  const [networkGraphData, setNetworkGraphData] =
    useState<NetworkGraphData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const filter = dateFilter || undefined;
      const [overviewData, networkData] = await Promise.all([
        reports.getPlatformOverview(filter, page, pageSize),
        reports.getNetworkGraph(filter),
      ]);
      setOverview(overviewData);
      setNetworkGraphData(networkData);
    } catch (error) {
      console.error("Erro ao carregar overview da plataforma", error);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, page, pageSize]);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    if (user.is_professional) {
      setIsModalOpen(true);
    } else {
      setIsStudentModalOpen(true);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (!user?.is_admin) {
      setLoading(false);
      return;
    }

    fetchOverview();
  }, [getUser, fetchOverview]);

  const user = getUser();
  if (!user?.is_admin) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 2xl:px-10">
        <PageMeta
          title="Dashboard Administrativo | Easyfit"
          description="Dashboard administrativo Easyfit"
        />
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          Acesso restrito a administradores.
        </div>
      </div>
    );
  }

  const totalPages =
    overview && overview.users.count > 0
      ? Math.ceil(overview.users.count / pageSize)
      : 1;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 2xl:px-10">
      <PageMeta
        title="Dashboard Administrativo | Easyfit"
        description="Dashboard administrativo Easyfit"
      />

      <AdminOverviewHeader
        dateFilter={dateFilter}
        onChangeFilter={(value) => {
          setPage(1);
          setDateFilter(value);
        }}
        onOpenNewProfessional={() => {
          setEditingUser(null);
          setIsModalOpen(true);
        }}
      />

      <NewProfessionalModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          fetchOverview();
        }}
        professional={editingUser}
      />

      <NewStudentModal
        isOpen={isStudentModalOpen}
        onClose={() => {
          setIsStudentModalOpen(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          setIsStudentModalOpen(false);
          setEditingUser(null);
          fetchOverview();
        }}
        student={editingUser}
      />

      {loading || !overview ? (
        <AdminOverviewSkeleton />
      ) : (
        <div className="space-y-8">
          <SummaryCardsSection overview={overview} />

          <GrowthChartsSection
            professionalsGrowth={overview.professionals_growth}
            studentsGrowth={overview.students_growth}
          />

          {networkGraphData && (
            <NetworkGraph data={networkGraphData} height={500} />
          )}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="xl:col-span-2 space-y-6">
              <GeneralMetricsCard
                summary={overview.summary}
                featureUsage={overview.feature_usage}
              />
            </div>

            <div className="space-y-4">
              <AdsEngagementCard
                advertisements={overview.advertisements}
                featureUsage={overview.feature_usage}
              />
            </div>
          </div>

          <UsersTableSection
            users={overview.users}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onEdit={handleEditUser}
          />

          {overview.top_exercises.length > 0 && (
            <TopExercisesSection exercises={overview.top_exercises} />
          )}
        </div>
      )}
    </div>
  );
}

interface AdminOverviewHeaderProps {
  dateFilter: number | null;
  onChangeFilter: (value: number) => void;
  onOpenNewProfessional?: () => void;
}

function AdminOverviewHeader({
  dateFilter,
  onChangeFilter,
  onOpenNewProfessional,
}: AdminOverviewHeaderProps) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-3xl font-semibold text-black dark:text-white">
          Dashboard Administrativo
        </h2>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Visão geral da plataforma, métricas globais e engajamento dos alunos.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {onOpenNewProfessional && (
          <button
            onClick={onOpenNewProfessional}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 pr-6"
          >
            <FiPlus />
            Novo Profissional
          </button>
        )}
        {filterOptions.map((option) => (
          <button
            key={option.label}
            onClick={() => onChangeFilter(option.value)}
            className={`rounded-md px-3 py-1 text-sm font-medium transition ${
              dateFilter === option.value
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-meta-4 dark:text-gray-300 dark:hover:bg-opacity-80"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function AdminOverviewSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:gap-7.5">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] md:p-6"
          >
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="mt-4 flex items-end justify-between">
              <div>
                <Skeleton className="mb-2 h-6 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <Skeleton className="mb-4 h-6 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <Skeleton className="mb-4 h-6 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

interface SummaryCardsSectionProps {
  overview: PlatformOverviewResponse;
}

interface EnhancedSummaryCardProps {
  icon: JSX.Element;
  title: string;
  value: number;
  subtitle: string;
  gradient: string;
  iconBg: string;
  shadowColor: string;
}

function EnhancedSummaryCard({
  icon,
  title,
  value,
  subtitle,
  gradient,
  iconBg,
  shadowColor,
}: EnhancedSummaryCardProps) {
  return (
    <div className="group rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm transition-all duration-300 hover:shadow-xl dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01] md:p-6">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
        style={{
          background: iconBg,
          boxShadow: `0 10px 25px -5px ${shadowColor}`,
        }}
      >
        {icon}
      </div>
      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {title}
        </p>
        <h4
          className="mt-2 bg-clip-text text-3xl font-bold text-transparent"
          style={{ backgroundImage: gradient }}
        >
          {value}
        </h4>
        <p className="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

function SummaryCardsSection({ overview }: SummaryCardsSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:gap-7.5">
      <EnhancedSummaryCard
        icon={<FiUsers className="h-7 w-7 text-white" />}
        title="Usuários totais"
        value={overview.summary.total_users}
        subtitle="Profissionais e alunos"
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        iconBg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        shadowColor="rgba(102, 126, 234, 0.5)"
      />
      <EnhancedSummaryCard
        icon={<FiBarChart2 className="h-7 w-7 text-white" />}
        title="Profissionais"
        value={overview.summary.professionals}
        subtitle="Usuários marcados como profissional"
        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        iconBg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        shadowColor="rgba(240, 147, 251, 0.5)"
      />
      <EnhancedSummaryCard
        icon={<FiActivity className="h-7 w-7 text-white" />}
        title="Alunos ativos"
        value={overview.feature_usage.active_students}
        subtitle="Com vínculo vigente com algum profissional"
        gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        iconBg="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        shadowColor="rgba(79, 172, 254, 0.5)"
      />
      <EnhancedSummaryCard
        icon={<FiTrendingUp className="h-7 w-7 text-white" />}
        title="Exercícios concluídos"
        value={overview.feature_usage.total_workouts}
        subtitle="No período selecionado"
        gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
        iconBg="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
        shadowColor="rgba(250, 112, 154, 0.5)"
      />
    </div>
  );
}

interface GeneralMetricsCardProps {
  summary: PlatformOverviewResponse["summary"];
  featureUsage: PlatformOverviewResponse["feature_usage"];
}

function GeneralMetricsCard({
  summary,
  featureUsage,
}: GeneralMetricsCardProps) {
  const metrics = [
    {
      label: "Planos de treino",
      value: summary.workout_plans,
      gradient: "from-violet-500 to-purple-600",
      bgGradient:
        "from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30",
    },
    {
      label: "Agendamentos",
      value: summary.appointments,
      gradient: "from-blue-500 to-cyan-600",
      bgGradient:
        "from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30",
    },
    {
      label: "Avaliações físicas",
      value: summary.avaliations,
      gradient: "from-emerald-500 to-teal-600",
      bgGradient:
        "from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
    },
    {
      label: "Exercícios cadastrados",
      value: summary.exercises,
      gradient: "from-amber-500 to-orange-600",
      bgGradient:
        "from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30",
    },
    {
      label: "Planos de assinatura",
      value: summary.plans,
      gradient: "from-pink-500 to-rose-600",
      bgGradient:
        "from-pink-50 to-rose-50 dark:from-pink-950/30 dark:to-rose-950/30",
    },
    {
      label: "Logs de uso",
      value: featureUsage.total_user_events,
      gradient: "from-indigo-500 to-purple-600",
      bgGradient:
        "from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30",
    },
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01]">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-lg font-bold text-transparent dark:from-purple-400 dark:to-pink-400">
          Métricas gerais
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`group rounded-xl bg-gradient-to-br ${metric.bgGradient} p-4 transition-all duration-300 hover:shadow-lg`}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
              {metric.label}
            </p>
            <p
              className={`mt-2 bg-gradient-to-r ${metric.gradient} bg-clip-text text-2xl font-bold text-transparent`}
            >
              {metric.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AdsEngagementCardProps {
  advertisements: PlatformOverviewResponse["advertisements"];
  featureUsage: PlatformOverviewResponse["feature_usage"];
}

function AdsEngagementCard({
  advertisements,
  featureUsage,
}: AdsEngagementCardProps) {
  const engagementMetrics = [
    {
      label: "Anúncios ativos",
      value: advertisements.total_ads,
      gradient: "from-purple-500 to-pink-500",
    },
    {
      label: "Visualizações",
      value: advertisements.total_views,
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      label: "Cliques",
      value: advertisements.total_clicks,
      gradient: "from-green-500 to-emerald-500",
    },
    {
      label: "Registros alimentação",
      value: featureUsage.total_food_logs,
      gradient: "from-orange-500 to-red-500",
    },
    {
      label: "Alunos engajados",
      value: featureUsage.engaged_students,
      gradient: "from-indigo-500 to-purple-500",
    },
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01]">
      <h3 className="mb-4 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-md font-bold text-transparent dark:from-pink-400 dark:to-purple-400">
        Anúncios e engajamento
      </h3>
      <div className="space-y-3 text-sm">
        {engagementMetrics.map((metric, index) => (
          <div
            key={index}
            className="group flex items-center justify-between rounded-lg bg-gradient-to-r from-gray-50 to-white p-3 transition-all duration-300 hover:shadow-md dark:from-gray-900/40 dark:to-gray-800/40"
          >
            <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <span className="font-medium">{metric.label}</span>
            </span>
            <span
              className={`bg-gradient-to-r ${metric.gradient} bg-clip-text text-lg font-bold text-transparent`}
            >
              {metric.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface GrowthChartsSectionProps {
  professionalsGrowth: PlatformOverviewResponse["professionals_growth"];
  studentsGrowth: PlatformOverviewResponse["students_growth"];
}

interface GrowthChartCardProps {
  title: string;
  description: string;
  data: PlatformOverviewResponse["professionals_growth"];
  suffix: string;
  emptyMessage: string;
  color?: string;
  barName?: string;
}

function GrowthChartCard({
  title,
  description,
  data,
  suffix,
  emptyMessage,
  color,
  barName,
}: GrowthChartCardProps) {
  if (data.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03] h-full">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  // Transform data for DashboardBarChart
  // Take last 7 days/items
  const slicedData = data.slice(-7);
  const categories = slicedData.map((d) =>
    d.date.split("-").reverse().slice(0, 2).join("/"),
  );
  const values = slicedData.map((d) => d.count);

  return (
    <div className="h-[400px]">
      <DashboardBarChart
        title={title}
        data={values}
        categories={categories}
        color={color}
        barName={barName || suffix}
      />
    </div>
  );
}

function GrowthChartsSection({
  professionalsGrowth,
  studentsGrowth,
}: GrowthChartsSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <GrowthChartCard
        title="Entrada de profissionais"
        description="Novos profissionais cadastrados no período selecionado"
        data={professionalsGrowth}
        suffix=" profissionais"
        emptyMessage="Ainda não há dados suficientes de entrada de profissionais para este período."
        color="#9333ea"
        barName="Profissionais"
      />
      <GrowthChartCard
        title="Entrada de alunos"
        description="Novos alunos cadastrados no período selecionado"
        data={studentsGrowth}
        suffix=" alunos"
        emptyMessage="Ainda não há dados suficientes de entrada de alunos para este período."
        color="#3b82f6"
        barName="Alunos"
      />
    </div>
  );
}

interface UsersTableSectionProps {
  users: PlatformOverviewResponse["users"];
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEdit: (user: User) => void;
}

function UsersTableSection({
  users,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onEdit,
}: UsersTableSectionProps) {
  const hasNextPage = users.results.length >= pageSize;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Usuários da plataforma
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Paginação baseada em {pageSize} usuários por página
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Nome
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Tipo
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Validade
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Registro
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900/40">
            {users.results.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Nenhum usuário encontrado para este período.
                </td>
              </tr>
            ) : (
              users.results.map((u) => (
                <tr key={u.id}>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {u.id}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">
                    {u.name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                    {u.email}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {u.is_professional ? "Profissional" : "Aluno"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {u.user_validity
                      ? new Date(u.user_validity).toLocaleDateString("pt-BR", {
                          timeZone: "UTC",
                        })
                      : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {u.registe
                      ? new Date(u.registe).toLocaleDateString("pt-BR")
                      : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                    <button
                      onClick={() => onEdit(u)}
                      className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
                      title="Editar usuário"
                    >
                      <FiEdit className="h-4 w-4 text-primary" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col items-center justify-center gap-3 border-t border-stroke pt-4 text-xs sm:flex-row sm:justify-between dark:border-strokedark">
        <div className="hidden sm:block text-gray-600 dark:text-gray-400">
          Página {page} de {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(page > 1 ? page - 1 : 1)}
            disabled={page === 1}
            className="inline-flex items-center gap-1.5 rounded-lg border border-stroke px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-boxdark"
          >
            Anterior
          </button>
          <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-bold text-purple-900 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
            Página {page}
          </div>
          <button
            type="button"
            onClick={() => onPageChange(hasNextPage ? page + 1 : page)}
            disabled={!hasNextPage}
            className="inline-flex items-center gap-1.5 rounded-lg border border-stroke px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-boxdark"
          >
            Próximo
          </button>
        </div>
        <div className="sm:hidden text-gray-600 dark:text-gray-400">
          Até {pageSize} por página
        </div>
      </div>
    </div>
  );
}

interface TopExercisesSectionProps {
  exercises: PlatformOverviewResponse["top_exercises"];
}

function TopExercisesSection({ exercises }: TopExercisesSectionProps) {
  const getRankColors = (index: number) => {
    if (index === 0) {
      return {
        gradient: "from-yellow-400 to-orange-500",
        bg: "from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30",
        border: "border-yellow-200 dark:border-yellow-800",
        badge: "from-yellow-400 to-orange-500",
      };
    } else if (index === 1) {
      return {
        gradient: "from-gray-300 to-gray-400",
        bg: "from-gray-50 to-slate-50 dark:from-gray-950/30 dark:to-slate-950/30",
        border: "border-gray-200 dark:border-gray-700",
        badge: "from-gray-400 to-gray-500",
      };
    } else if (index === 2) {
      return {
        gradient: "from-orange-400 to-amber-600",
        bg: "from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30",
        border: "border-orange-200 dark:border-orange-800",
        badge: "from-orange-400 to-amber-600",
      };
    } else {
      return {
        gradient: "from-purple-500 to-pink-500",
        bg: "from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20",
        border: "border-purple-100 dark:border-purple-900",
        badge: "from-purple-500 to-pink-500",
      };
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01]">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-lg font-bold text-transparent dark:from-purple-400 dark:to-pink-400">
            Top 10 Exercícios
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Os mais executados no período selecionado
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        {exercises.slice(0, 10).map((exercise, index) => {
          const colors = getRankColors(index);
          return (
            <div
              key={exercise.exercise_id}
              className={`group flex items-center justify-between rounded-xl border ${colors.border} bg-gradient-to-r ${colors.bg} p-4 text-sm transition-all duration-300 hover:shadow-lg`}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br ${colors.gradient} text-sm font-bold text-white shadow-lg`}
                  >
                    {index + 1}
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {exercise.exercise_name}
                  </p>
                  <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                    ID {exercise.exercise_id}
                  </p>
                </div>
              </div>
              <span
                className={`rounded-full bg-gradient-to-r ${colors.badge} px-4 py-2 text-xs font-bold text-white shadow-md`}
              >
                {exercise.count} execuções
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
